import {WindowDimensions} from '../types/window-dimensions';
import {ScrollPosition} from '../types/scroll-position';
import {WindowPosition} from '../types/window-position';
import mitt, {Emitter} from 'mitt';

// Type for browser events
type BrowserEvent = {
  resize: WindowDimensions,
  scroll: ScrollPosition,
  orientation: string
};

/**
 * Contains general utility methods.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class GeneralUtility {
  // Current page UID
  private static _pageUid: number;

  // General settings
  private static _settings: any;

  // Current page UID rootline
  private static _rootline: number[];

  // Emits browser related events
  public static browserEmitter: Emitter<BrowserEvent> = mitt<BrowserEvent>();

  /**
   * Gets a property from the `Global` variable. If the property is not found, an error is thrown.
   *
   * @param {string} name Name of the property to get
   * @returns {any} Property value, if found
   */
  public static getGlobalProperty(name: string): any {
    if (Globals === undefined || !Globals) {
      throw new Error('Globals are missing.');
    }
    if (Globals[name] === undefined || !Globals.hasOwnProperty(name)) {
      throw new Error('Property ' + name + ' not found in globals.');
    }
    return Globals[name];
  }

  public static get pageUid(): number {
    if (GeneralUtility._pageUid === undefined) {
      GeneralUtility._pageUid = parseInt(GeneralUtility.getGlobalProperty('pageUid'));
    }
    return GeneralUtility._pageUid;
  }

  public static get settings(): any {
    if (GeneralUtility._settings === undefined) {
      GeneralUtility._settings = GeneralUtility.getGlobalProperty('settings');
    }
    return GeneralUtility._settings;
  }

  public static get rootline(): number[] {
    if (GeneralUtility._rootline === undefined) {
      GeneralUtility._rootline = GeneralUtility.getGlobalProperty('rootline');
    }
    return GeneralUtility._rootline;
  }

  /**
   * Gets the current page URL.
   *
   * @param {string} [additionalParameters=[]] Additional GET parameters to add to the URL
   * @param {boolean} [keepParameters=false] Whether to keep current URL parameters, or to throw them away
   * @param {boolean} [keepHash=false] Whether to keep current URL hash, or to throw it away
   * @returns {string} Current URL without a trailing slash
   */
  public static getUrl(
    additionalParameters: string[] = [],
    keepParameters: boolean = false,
    keepHash: boolean = false
  ): string {
    // Parse base URL
    const originalUrl = window.location.href;
    let baseUrl = originalUrl.split('?')[0].split('#')[0];
    if (baseUrl.charAt(baseUrl.length - 1) === '/') {
      baseUrl = baseUrl.substring(0, baseUrl.length - 1);
    }
    const hash = window.location.hash;

    // Extract current parameters
    let parameters = '';
    const parts = originalUrl.match(new RegExp(/\?(.*)$/));
    if (parts !== null && parts.length === 2) {
      parameters = parts[1].replace(hash, '');
    }

    // Build the final URL
    let url = baseUrl;
    if (keepParameters && parameters !== '') {
      url += `?${parameters}`;
    }
    if (additionalParameters.length > 0) {
      url += (url.includes('?') ? '&' : '?') + additionalParameters.join('&');
    }
    if (keepHash && hash !== '') {
      url += hash;
    }

    return url;
  }

  /**
   * Checks if the current devices is a touch device. Note that devices with multi-touch capable touchpads like laptops
   * will also be detected as mobile.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#Mobile_Device_Detection
   * @returns {boolean}
   */
  public static isTouch(): boolean {
    let hasTouchScreen: boolean;
    if ('maxTouchPoints' in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else if ('msMaxTouchPoints' in navigator) {
      // @ts-ignore
      hasTouchScreen = navigator.msMaxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia !== undefined && window.matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        hasTouchScreen = !!mQ.matches;
      } else if ('orientation' in window) {
        hasTouchScreen = true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        // @ts-ignore
        const UA = navigator.userAgent;
        hasTouchScreen = (
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
        );
      }
    }

    return hasTouchScreen;
  }

  /**
   * Checks if the current device prefers reduced motion.
   *
   * @returns {boolean}
   */
  public static isReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.location.hash.includes('reduced-motion');
  }

  /**
   * Gets the current device orientation.
   *
   * @returns {string} "portrait" or "landscape"
   */
  public static getDeviceOrientation(): string {
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    } else {
      return 'landscape';
    }
  }

  /**
   * Gets the current window dimensions.
   *
   * @returns {WindowDimensions}
   */
  public static getWindowDimensions(): WindowDimensions {
    return {
      width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
      height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
    };
  }

  /**
   * Gets the current window scroll position.
   *
   * @returns {ScrollPosition}
   */
  public static getScrollPosition(): ScrollPosition {
    const windowDimensions = GeneralUtility.getWindowDimensions();
    return {
      previousScrollY: 0,
      scrollY: window.scrollY,
      scrollYBottom: window.scrollY + windowDimensions.height,
      previousScrollX: 0,
      scrollX: window.scrollX,
      scrollXRight: window.scrollX + windowDimensions.width
    };
  }

  /**
   * Observes the intersection of an element.
   *
   * @param {HTMLElement} element Element to observe
   * @param {function} callback Callback when the search changes
   * @param {Partial<IntersectionObserverInit>} [options] Observer options
   * @param {number} [timeout] Timeout in ms to wait before observing interaction; can be useful if the page structure
   * changes rapidly, e.g. hiding/displaying elements quickly, which may otherwise trigger the observer too early
   */
  public static observeIntersection(
    element: HTMLElement,
    callback: (entry: IntersectionObserverEntry) => void,
    options: Partial<IntersectionObserverInit> = {},
    timeout: number = 0): void {
    const defaultOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 1
    };
    options = Object.assign(
      {},
      defaultOptions,
      options
    );
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          callback(entry);
        }
      },
      options
    );
    if (timeout > 0) {
      setTimeout(() => {
        observer.observe(element);
      }, timeout);
    } else {
      observer.observe(element);
    }
  }

  /**
   * Gets the current position of an element within the window.
   *
   * @param {HTMLElement} element Element to get offset from
   * @param {ScrollPosition} [scrollPosition] Current scroll position
   */
  public static getElementPosition(
    element: HTMLElement,
    scrollPosition: ScrollPosition = GeneralUtility.getScrollPosition()
  ): WindowPosition {
    const boundaries = element.getBoundingClientRect();
    return {
      top: boundaries.top + scrollPosition.scrollY,
      right: boundaries.right + scrollPosition.scrollX,
      bottom: boundaries.bottom + scrollPosition.scrollY,
      left: boundaries.left + scrollPosition.scrollX
    };
  }

  /**
   * Adds a class to a selector for a certain amount of time, then removes it again.
   *
   * @param {HTMLElement} element Element to add class to
   * @param {string} newClass Class to add
   * @param {number} [period] Live-Time of the class in ms, defaults to 1000ms
   */
  public static addTimedClass(element: HTMLElement, newClass: string, period: number = 1000): void {
    element.classList.add(newClass);
    setTimeout(() => {
      element.classList.remove(newClass);
    }, period);
  }

  /**
   * Implements a debounced text-seach on the given text input field.
   *
   * @param {HTMLInputElement} inputField Input field to implement search for
   * @param {function} callback Callback when the search changes
   * @param {number} [debounceTimeout] Debounce-delay in milliseconds (defaults to 500)
   * @param {function} [parse] Function to parse the input field value by (defaults to lowercasing and trimming the
   * string-value)
   */
  public static debouncedSearch(
    inputField: HTMLInputElement,
    callback: any,
    debounceTimeout: number = 500,
    parse: any = null
  ) {
    if (debounceTimeout === undefined) {
      debounceTimeout = 500;
    }

    let debounce: boolean = false;
    let timeout: any = null;

    /**
     * Checks if the given key is allowed.
     *
     * @param {number} keyCode Key-code to check
     * @returns {boolean} True when the key is allowed, otherwise false
     */
    const isAllowedKey = (keyCode: number): boolean => {
      const allowedKeyCodes: number[] = [
        8, // Backspace
        13, // Enter
        16, // Shift
        17, // Ctrl
        32, // Space
        46, // Delete
        106, // Multiply numpad
        109, // Minus numpad
        173, // Minus
        110, // Comma numpad
        188, // Comma
        59, // ü
        192, // ö
        222 // ä
      ];

      if (allowedKeyCodes.indexOf(keyCode) !== -1) {
        return true;
      }

      // 0-9
      else if (keyCode >= 48 && keyCode <= 57) {
        return true;
      }

      // 0-9 numpad
      else if (keyCode >= 96 && keyCode <= 105) {
        return true;
      }

      // a-z
      else if (keyCode >= 65 && keyCode <= 90) {
        return true;
      }

      return false;
    };

    inputField.addEventListener('keyup', (e) => {
      if (!isAllowedKey(e.keyCode) || debounce) {
        return;
      }

      if (timeout !== null) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        let value = inputField.value;

        if (parse) {
          value = parse(value);
        } else {
          value = value.toLowerCase().trim();
        }

        callback(value);

        debounce = false;
      }, debounceTimeout);

      debounce = true;
    });
  }

  /**
   * Copies text to the client's clipboard.
   *
   * @param {string} text Text to copy to the clipboard
   */
  public static copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    // Fallback for older browsers
    else {
      const textarea = document.createElement('textarea');
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.position = 'fixed';
      textarea.value = text;

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        const success = document.execCommand('copy');
        if (!success) {
          throw new Error('Copying to clipboard failed.');
        }
      } catch (error) {
        throw new Error('Browser does not support clipboard interaction.');
      }

      textarea.remove();
    }
  }

  /**
   * Gets a random integer within a specific range.
   *
   * @param {number} min Lower boundary (inclusive)
   * @param {number} max Upper boundary (inclusive)
   * @returns {number} Random integer within the boundaries (inclusive)
   */
  public static getRandomInteger(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Executes a delayed for loop.
   *
   * @param {number} iterations Amount of iterations to execute
   * @param {number} delay Delay between each iteration in milliseconds
   * @param {function} callback Callback executed for each iteration
   */
  public static delayedLoop(
    iterations: number,
    delay: number,
    callback: (iteration: number, finished: boolean) => void
  ): void {
    const sleep = (delay: number) => {
      return new Promise((resolve) => setTimeout(resolve, delay));
    };
    const loop = async () => {
      for (let i = 0; i < iterations; i++) {
        await sleep(delay);
        callback(i, i === iterations - 1);
      }
    };
    loop();
  }

  /**
   * Checks if a video is currently playing or not.
   *
   * @param {HTMLVideoElement} video
   * @returns {boolean} True when the video is playing and is not ended, otherwise false
   */
  public static isVideoPlaying(video: HTMLVideoElement): boolean {
    return !video.paused && !video.ended && video.currentTime > 0 && video.readyState > 2;
  }

  /**
   * Gets the height of the site header.
   *
   * @returns {number}
   */
  public static getHeaderHeight(): number {
    return document.querySelector('#header')?.clientHeight ?? 0;
  }

  /**
   * Triggers loading of resources defined in a lazy-image-sourceset handlebars partial.
   *
   * @param {HTMLElement} container Container of a lazy-image-sourceset, where the lazy-load-attributes will be used to
   * actually load resources
   */
  static loadLazyImageSourcesets(container: HTMLElement): void {
    const lazyImageSourcesets = container.querySelectorAll<HTMLElement>('picture[data-lazy="imageSourceset"]');
    lazyImageSourcesets.forEach((picture: HTMLElement) => {
      const sources = picture.querySelectorAll<HTMLElement>('source[data-srcset]');
      sources.forEach((source: HTMLElement) => {
        if (!source.dataset.srcset) {
          return;
        }
        source.setAttribute('srcset', source.dataset.srcset);
        delete source.dataset.srcset;
      });

      const imgs = picture.querySelectorAll<HTMLImageElement>('img[data-src]');
      imgs.forEach((img: HTMLElement) => {
        if (!img.dataset.src) {
          return;
        }
        img.setAttribute('src', img.dataset.src);
        delete img.dataset.src;
      });

      // Remove lazy-data attribute to prevent executing this method multiple times on the same element
      delete picture.dataset.lazy;
    });
  }
}
